/**
 * Google Analytics 4 integration module using react-ga4.
 *
 * Handles initialisation and provides utilities for tracking page views
 * and custom events. The Measurement ID is read from the
 * VITE_GA_MEASUREMENT_ID environment variable - if unset, all tracking
 * functions become no-ops.
 */

import ReactGA from "react-ga4"

// ─── Types ───────────────────────────────────────────────────────────────────

/** Parameters accepted by custom event calls. */
type EventParams = Record<string, string | number | boolean>

// ─── Initialisation ──────────────────────────────────────────────────────────

const MEASUREMENT_ID: string | undefined = import.meta.env.VITE_GA_MEASUREMENT_ID

/** Whether GA has been successfully initialised. */
let isInitialised = false

/**
 * Initialises Google Analytics 4 via react-ga4. This should be called once
 * on app startup (before any tracking calls).
 *
 * If VITE_GA_MEASUREMENT_ID is not set, this function does nothing and all
 * subsequent tracking calls will be no-ops.
 */
export function initializeGA(): void {
  if (!MEASUREMENT_ID) {
    return
  }

  ReactGA.initialize(MEASUREMENT_ID, {
    // Disable the automatic page_view that fires on initialisation —
    // we handle page views manually on route changes.
    gtagOptions: { send_page_view: false },
  })

  isInitialised = true
}

// ─── Event Helpers ───────────────────────────────────────────────────────────

/**
 * Sends a page_view event to GA4.
 *
 * @param pagePath - The route path (e.g. "/datasets").
 */
export function trackPageView(pagePath: string): void {
  if (!isInitialised) {
    return
  }

  ReactGA.send({ hitType: "pageview", page: pagePath })
}

/**
 * Sends a custom event to GA4.
 *
 * Use this for feature-usage tracking (chat messages, form submissions,
 * file uploads, etc.).
 *
 * @param eventName - A snake_case event name (e.g. "chat_message_sent").
 * @param params - Optional key-value pairs of event parameters.
 */
export function trackEvent(eventName: string, params?: EventParams): void {
  if (!isInitialised) {
    return
  }

  ReactGA.event(eventName, params)
}
